import { useCallback, useEffect, useState } from 'react'
import { Alert, Switch } from '@mantine/core'
import { useMedplum } from '@medplum/react'
import { useClient, type OrpcClient } from '@/hooks/useClient'
import { AdminPanel } from './AdminPanel'
import { HINT } from './tokens'

type SyncState = Awaited<ReturnType<OrpcClient['zocdocSyncState']>>

/**
 * The availability sync's three switches, and the only place all three are visible at once.
 *
 * They live in three different stores — a Medplum `Subscription`, a task row, and that task's
 * scheduled args — so before this card, answering "is the sync on?" meant opening Super → Tasks
 * *and* the Medplum admin app. For a feature whose failure mode is silence, that is the wrong
 * number of places to look.
 *
 * **Care coordinators see it; only super admins can change it.** A coordinator works this console
 * all day and needs to know whether publishing is live — it is the context for every other panel
 * here. Flipping a switch commits the practice to writing a doctor's calendar on a third-party
 * marketplace, which is not theirs to do. The switches render disabled rather than hidden, so the
 * state is still legible and it is obvious there is something to ask for.
 *
 * The middleware enforces the same split (`@AdminAuth()` to read, `@SuperAdminAuth()` to write) —
 * this only decides what to draw.
 */
export function SyncControlsCard() {
  const client = useClient()
  const medplum = useMedplum()
  const canEdit = medplum.isSuperAdmin()

  const [state, setState] = useState<SyncState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      setState(await client.zocdocSyncState())
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not read the sync state')
    }
  }, [client])

  useEffect(() => {
    void load()
  }, [load])

  const apply = async (changes: {
    subscriptionStatus?: 'active' | 'off'
    taskStatus?: 'active' | 'disabled'
    dryRun?: boolean
  }) => {
    setBusy(true)
    setError(null)
    try {
      setState(await client.zocdocSetSyncControls(changes))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not change the sync controls')
      // Re-read rather than trust the local guess: a refused write must not leave the switch
      // showing a position the server never took.
      await load()
    } finally {
      setBusy(false)
    }
  }

  const subscriptionOn = state?.subscription.status === 'active'
  const taskOn = state?.task.status === 'active'
  // Absent scheduled args mean a dry run, which is what the runner does too — so the switch shows
  // "live publishing" off rather than claiming a run would publish when it would not.
  const livePublishing = state ? !state.publishing.dryRun : false
  const subscriptionBlocked = state?.subscription.status === 'absent'

  return (
    <AdminPanel
      title="Availability sync"
      description="Publishes beHuman availability to Zocdoc. Three independent switches — all three must be on for a calendar change to reach Zocdoc."
    >
      <div className="flex flex-col gap-3">
        {error && (
          <Alert color="red" radius="md">
            {error}
          </Alert>
        )}

        {!canEdit && (
          <p className={HINT}>
            Read-only. Changing these is a super-admin action — each one commits the practice to
            writing a practitioner’s calendar on Zocdoc.
          </p>
        )}

        <Row
          label="Slot subscription"
          help="Medplum tells middleware when a practitioner’s calendar changes. Off means nothing is republished automatically; the reconcile task still catches up on its own schedule."
          checked={subscriptionOn}
          disabled={!canEdit || busy || subscriptionBlocked}
          onChange={(next) => void apply({ subscriptionStatus: next ? 'active' : 'off' })}
          note={
            state?.subscription.blockedReason ??
            (subscriptionBlocked
              ? 'No subscription exists yet — run project configure.'
              : undefined)
          }
          status={state?.subscription.status}
        />

        <Row
          label="Reconcile task"
          help="Compares beHuman’s calendar against what Zocdoc holds and republishes the days that disagree. This is what catches deleted slots, which a subscription cannot see."
          checked={taskOn}
          disabled={!canEdit || busy}
          onChange={(next) => void apply({ taskStatus: next ? 'active' : 'disabled' })}
          note={
            state?.task.lastRunAt
              ? `Last run ${new Date(state.task.lastRunAt).toLocaleString()} — ${state.task.lastRunStatus ?? 'unknown'}${state.task.lastRunSummary ? `: ${state.task.lastRunSummary}` : ''}`
              : 'Never run'
          }
          status={state?.task.status}
        />

        <Row
          label="Live publishing"
          help="Off means every run builds and checks the payload but sends nothing. Leave it off until a dry run reads the way you expect — each publish replaces a whole day of a doctor’s Zocdoc availability."
          checked={livePublishing}
          disabled={!canEdit || busy}
          onChange={(next) => void apply({ dryRun: !next })}
          status={livePublishing ? 'publishing' : 'dry run'}
        />
      </div>
    </AdminPanel>
  )
}

function Row({
  label,
  help,
  checked,
  disabled,
  onChange,
  note,
  status,
}: {
  label: string
  help: string
  checked: boolean
  disabled: boolean
  onChange: (next: boolean) => void
  note?: string
  status?: string
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-[#EFEFEF] pb-3 last:border-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-bold">{label}</span>
          {status && <span className={HINT}>{status}</span>}
        </div>
        <p className={`${HINT} m-0 mt-1`}>{help}</p>
        {note && <p className={`${HINT} m-0 mt-1`}>{note}</p>}
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.checked)}
        aria-label={label}
      />
    </div>
  )
}
